import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

// Section head: a terracotta dot, the title in the display serif, and a thin
// warm rule ruled underneath the whole width.
function SectionHeader({ title, to }) {
    return (
        <div className="mb-9">
            <div className="flex items-end justify-between gap-6">
                <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-accent shrink-0" />
                    <h2 className="font-display text-[1.7rem] sm:text-[2rem] font-semibold leading-none text-foreground">
                        {title}
                    </h2>
                </div>
                {to && (
                    <Link
                        to={to}
                        className="eyebrow shrink-0 text-dim hover:text-accent transition-colors duration-200 link-underline"
                    >
                        View all
                    </Link>
                )}
            </div>
            <div className="h-px bg-line mt-5" />
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <p className="flex items-center justify-center gap-2.5 font-mono text-xs text-dim mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                Loading the newsroom…
            </p>
            <div className="shimmer aspect-[16/9] rounded-2xl border border-line" />
            <div className="max-w-3xl mx-auto mt-10 space-y-4">
                <div className="shimmer h-3 w-24 rounded-full mx-auto" />
                <div className="shimmer h-9 w-full rounded-lg" />
                <div className="shimmer h-9 w-2/3 rounded-lg mx-auto" />
                <div className="shimmer h-4 w-1/2 rounded-full mx-auto mt-6" />
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-28" data-testid="home-empty">
                <div className="panel max-w-lg mx-auto p-14 text-center">
                    <span className="block h-0.5 w-10 mx-auto rounded-full bg-accent" />
                    <span className="eyebrow block mt-5 text-accent">Standby</span>
                    <h2 className="font-display text-[1.9rem] font-semibold leading-tight mt-4">Fetching the latest stories</h2>
                    <p className="mt-4 text-[15px] text-dim leading-relaxed">
                        Articles are being pulled from The Guardian. Please check back in a moment.
                    </p>
                </div>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-page">
            {/* Lead plate, headline set beneath it */}
            <div className="py-16 sm:py-20">
                <ArticleCard article={featured} variant="hero" />
            </div>

            {/* Latest — three columns of rule-topped items */}
            <section className="pb-20 sm:pb-24">
                <SectionHeader title="Latest News" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
                    {latest.slice(0, 6).map((a) => (
                        <div key={a.id} className="border-t border-line">
                            <ArticleCard article={a} variant="compact" />
                        </div>
                    ))}
                </div>
            </section>

            {/* Sections + trending rail */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 pb-24 sm:pb-28">
                <div className="lg:col-span-8 space-y-20 sm:space-y-24">
                    {Object.entries(sections).map(([cat, arts]) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader title={CATEGORY_LABELS[cat] || cat} to={`/category/${cat}`} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
